/**
 * Utilities for working with Opus-style editingScript in the frontend.
 *
 * This module provides functions to:
 * 1. Convert legacy clip data to editingScript format
 * 2. Extract data from tracks for component consumption
 * 3. Update tracks within an editingScript
 */

import { v4 as uuidv4 } from "uuid"
import { toCaptionTimedWord } from "@/lib/caption-utils"
import {
  DEFAULT_GIF_POSITION,
  DEFAULT_CAPTION_STYLE_CONFIG,
  DEFAULT_RENDER_PREFERENCES,
} from "@/remotion/schemas"
import type {
  EditingScript,
  Track,
  TrackType,
  Section,
  Segment,
  Duration,
  Timeline,
  Position,
  CaptionSegment,
  HookSegment,
  BRollSegmentType,
  MusicSegment,
  MusicMediaInfo,
  MusicAudioSettings,
  GifSegmentType,
  GifAudioSettings,
  VoiceoverSegment,
  CaptionStyleConfig,
  TextStyle,
} from "@/remotion/schemas"
import type { Caption, CaptionStyle } from "@/types/caption"
import type { BRollSegment } from "@/app/clips/[contentId]/edit/[clipId]/components/BRollTrack"
import { normalizeCuts } from "@/remotion/utils/cuts"

/**
 * A script made safe to open and preview: cuts sorted, merged and clamped to the clip; overlay and audio
 * pieces that don't run forward (zero, negative or non-finite length) dropped; negative media offsets
 * cleared. Captions are left alone (they are rebuilt from the words). A script saved by an older build
 * can't brick the editor this way.
 */
export function sanitizeEditingScript(script: EditingScript): EditingScript {
  const runsForward = (segment: Section["segments"][number]) => {
    const timeline = (segment as { timeline?: Timeline }).timeline
    return !timeline || (Number.isFinite(timeline.in) && Number.isFinite(timeline.out) && timeline.out > timeline.in)
  }
  const withoutNegativeTrim = (segment: Section["segments"][number]) =>
    ((segment as { trimStartMs?: number }).trimStartMs ?? 0) < 0 ? { ...segment, trimStartMs: 0 } : segment
  return {
    ...script,
    ...(script.cuts && { cuts: normalizeCuts(script.cuts, script.durationMs || undefined) }),
    tracks: script.tracks.map((track) => track.trackType === "CaptionTrack" ? track : {
      ...track,
      sections: track.sections.map((section) => ({
        ...section,
        segments: section.segments.filter(runsForward).map(withoutNegativeTrim),
      })),
    }),
  }
}

// =============================================================================
// ID Generation
// =============================================================================

/**
 * Generate a short random ID with optional prefix.
 */
export function generateId(prefix: string = ""): string {
  const shortId = uuidv4().slice(0, 6)
  return prefix ? `${prefix}-${shortId}` : shortId
}

// =============================================================================
// Time Conversion
// =============================================================================

/**
 * Convert seconds to milliseconds.
 */
export function secondsToMs(seconds: number): number {
  return Math.floor(seconds * 1000)
}

/**
 * Convert milliseconds to seconds.
 */
export function msToSeconds(ms: number): number {
  return ms / 1000
}

// =============================================================================
// Duration/Timeline Builders
// =============================================================================

/**
 * Build a Duration object.
 */
export function buildDuration(startMs: number, endMs: number): Duration {
  return {
    sO: startMs,
    eO: endMs,
    type: "TS",
  }
}

/**
 * Build a Timeline object.
 */
export function buildTimeline(startMs: number, endMs: number): Timeline {
  return {
    in: startMs,
    out: endMs,
  }
}

/**
 * Build a Position object.
 */
export function buildPosition(
  xPct: number = 0,
  yPct: number = 0,
  wPct: number = 0.9,
  hPct: number = 0.1
): Position {
  return {
    type: "CPct",
    xPct,
    yPct,
    wPct,
    hPct,
  }
}

// =============================================================================
// Track Access
// =============================================================================

/**
 * Get a specific track by type from editingScript.
 */
export function getTrackByType(
  editingScript: EditingScript | null | undefined,
  trackType: TrackType
): Track | undefined {
  if (!editingScript) return undefined
  return editingScript.tracks.find((track) => track.trackType === trackType)
}

/**
 * Get all segments from a track.
 */
export function getSegmentsFromTrack<T extends Segment>(track: Track | undefined): T[] {
  if (!track) return []
  const segments: T[] = []
  for (const section of track.sections) {
    segments.push(...(section.segments as T[]))
  }
  return segments
}

// =============================================================================
// Caption Extraction
// =============================================================================

/**
 * Extract Caption[] from CaptionTrack for legacy component compatibility.
 */
export function extractCaptionsFromTrack(track: Track | undefined): Caption[] {
  if (!track || track.trackType !== "CaptionTrack") return []

  const captions: Caption[] = []

  for (const section of track.sections) {
    for (const segment of section.segments) {
      if (segment.contentType === "Caption") {
        const captionSegment = segment as CaptionSegment
        const content = captionSegment.content
        const timeline = captionSegment.timeline

        // Build caption text from text elements
        const text = content.textElements.map((te) => te.text).join(" ")

        // Build words array
        // Elements span the word's start (in) to its end (out); time_stamp is the end.
        const words = content.textElements.map((te) => ({
          word: te.text,
          start_time: msToSeconds(te.timeline.in),
          time_stamp: msToSeconds(te.timeline.out),
          start_position: 0,
          end_position: 0,
        }))

        captions.push({
          id: captionSegment.id,
          text,
          startTime: msToSeconds(timeline.in),
          endTime: msToSeconds(timeline.out),
          words,
        })
      }
    }
  }

  return captions
}

/**
 * Extract CaptionStyle from CaptionTrack's styleConfig.
 */
export function extractStyleFromTrack(track: Track | undefined): CaptionStyle | null {
  if (!track || track.trackType !== "CaptionTrack" || !track.styleConfig) {
    return null
  }

  const config = track.styleConfig

  return {
    wordsPerCaption: config.wordsPerCaption,
    color: config.colors.default,
    backgroundColor: config.backdrop?.enabled ? config.backdrop.color : "transparent",
    fontSize: config.font.size,
    fontFamily: config.font.family,
    fontWeight: config.font.weight,
    fontStyle: config.font.style as "normal" | "italic",
    textAlign: "center",
    strokeWidth: config.stroke.enabled ? config.stroke.width : 0,
    strokeColor: config.stroke.color,
    lineHeight: config.lineHeight,
    position: config.position as CaptionStyle["position"],
    positionOffset: config.positionOffset,
    textTransform: config.textTransform as "none" | "uppercase" | "lowercase" | "capitalize",
    animation: config.animation as any,
    activeWordEnabled: config.activeWordEnabled,
    activeWordColor: config.colors.primary,
    activeWordBackgroundColor: "transparent",
    activeWordStrokeWidth: config.activeWordStrokeWidth ?? 0,
    activeWordStrokeColor: config.activeWordStrokeColor ?? "#000000",
    shadowEnabled: config.shadow.enabled,
    shadowColor: config.shadow.color,
    shadowBlur: config.shadow.blur,
    shadowOffsetX: config.shadow.x,
    shadowOffsetY: config.shadow.y,
    keywordHighlightEnabled: config.keywordHighlightEnabled,
    keywordColor: config.keywordColor,
    aiEmojiEnabled: config.aiEmojiEnabled,
    positionX: config.positionX,
    positionY: config.positionY,
    maxLines: config.maxLines,
    maskProfanity: config.maskProfanity,
  }
}

// =============================================================================
// Hook/Text Overlay Extraction
// =============================================================================

/**
 * Extract HookSegment[] from HookTrack.
 */
export function extractHooksFromTrack(track: Track | undefined): HookSegment[] {
  if (!track || track.trackType !== "HookTrack") return []
  return getSegmentsFromTrack<HookSegment>(track)
}

// =============================================================================
// B-Roll Extraction
// =============================================================================

/**
 * Extract BRollSegment[] from BRollTrack for legacy component compatibility.
 */
export function extractBRollFromTrack(track: Track | undefined): BRollSegment[] {
  if (!track || track.trackType !== "BRollTrack") return []

  const segments: BRollSegment[] = []

  for (const section of track.sections) {
    for (const segment of section.segments) {
      if (segment.contentType === "BRoll") {
        const brollSegment = segment as BRollSegmentType
        const rawMediaInfo = brollSegment.content.mediaInfo as unknown as Record<string, unknown>

        // Normalize mediaInfo to handle snake_case from backend
        // Note: Keep originUrl/previewUrl as actual media URLs (for overlay playback)
        // Keep coverImage separate (for timeline thumbnails)
        const mediaInfo = {
          mediaType: (rawMediaInfo.mediaType || rawMediaInfo.media_type || "image") as string,
          originUrl: (rawMediaInfo.originUrl || rawMediaInfo.origin_url || "") as string,
          previewUrl: (rawMediaInfo.previewUrl || rawMediaInfo.preview_url || "") as string,
          width: (rawMediaInfo.width || 1080) as number,
          height: (rawMediaInfo.height || 1920) as number,
          brollType: (rawMediaInfo.brollType || rawMediaInfo.broll_type) as string | undefined,
          sourceId: (rawMediaInfo.sourceId || rawMediaInfo.source_id) as string | undefined,
          duration: (rawMediaInfo.duration) as number | undefined,
          coverImage: (rawMediaInfo.coverImage || rawMediaInfo.cover_image) as string | undefined,
        }

        segments.push({
          id: brollSegment.id,
          startMs: brollSegment.timeline.in,
          endMs: brollSegment.timeline.out,
          trimStartMs: brollSegment.trimStartMs,
          brollType: brollSegment.content.brollType,
          mediaInfo,
          prompt: brollSegment.content.prompt,
        })
      }
    }
  }

  return segments
}

// =============================================================================
// Track Updates
// =============================================================================

/**
 * Update a track in the editingScript.
 */
export function updateTrackInScript(
  editingScript: EditingScript,
  updatedTrack: Track
): EditingScript {
  const newTracks = editingScript.tracks.map((track) =>
    track.trackType === updatedTrack.trackType ? updatedTrack : track
  )

  return {
    ...editingScript,
    tracks: newTracks,
  }
}

/**
 * Add a segment to a track.
 */
export function addSegmentToTrack(
  track: Track,
  segment: Segment
): Track {
  // If no sections exist, create one
  if (track.sections.length === 0) {
    const section: Section = {
      id: generateId("section"),
      sectionDuration: segment.duration,
      sectionTimeline: segment.timeline,
      segments: [segment],
    }
    return { ...track, sections: [section] }
  }

  // Add to the first section
  const firstSection = track.sections[0]
  const updatedSection: Section = {
    ...firstSection,
    segments: [...firstSection.segments, segment],
  }

  return {
    ...track,
    sections: [updatedSection, ...track.sections.slice(1)],
  }
}

/**
 * Remove a segment from a track by ID.
 */
export function removeSegmentFromTrack(track: Track, segmentId: string): Track {
  const newSections = track.sections.map((section) => ({
    ...section,
    segments: section.segments.filter((seg) => seg.id !== segmentId),
  }))

  return { ...track, sections: newSections }
}

/**
 * Update a segment in a track.
 */
export function updateSegmentInTrack(
  track: Track,
  segmentId: string,
  updates: Partial<Segment>
): Track {
  const newSections = track.sections.map((section) => ({
    ...section,
    segments: section.segments.map((seg) =>
      seg.id === segmentId ? { ...seg, ...updates } : seg
    ),
  }))

  return { ...track, sections: newSections }
}

// =============================================================================
// Legacy Conversion
// =============================================================================

/**
 * Convert CaptionStyle to CaptionStyleConfig.
 */
export function convertStyleToConfig(style: CaptionStyle): CaptionStyleConfig {
  const hasBackdrop =
    !!style.backgroundColor &&
    style.backgroundColor !== "transparent" &&
    style.backgroundColor !== ""

  return {
    font: {
      family: style.fontFamily || "Montserrat",
      size: style.fontSize || 50,
      weight: style.fontWeight || "900",
      style: style.fontStyle || "normal",
    },
    colors: {
      default: style.color || "#FFFFFF",
      primary: style.activeWordColor || "#04F827",
      secondary: "#FFFD03",
    },
    stroke: {
      enabled: (style.strokeWidth || 0) > 0,
      color: style.strokeColor || "#000000",
      width: style.strokeWidth || 3,
    },
    shadow: {
      enabled: style.shadowEnabled || false,
      color: style.shadowColor || "#000000",
      x: style.shadowOffsetX || 2,
      y: style.shadowOffsetY || 2,
      blur: style.shadowBlur || 4,
    },
    backdrop: {
      enabled: hasBackdrop,
      color: hasBackdrop ? style.backgroundColor : "rgba(0,0,0,0.55)",
      paddingX: 16,
      paddingY: 6,
      radius: 14,
    },
    animation: style.animation || "none",
    textTransform: style.textTransform || "none",
    position: style.position || "bottom",
    positionOffset: style.positionOffset || 10,
    wordsPerCaption: style.wordsPerCaption || 5,
    activeWordEnabled: style.activeWordEnabled ?? true,
    lineHeight: style.lineHeight || 1.2,
    keywordHighlightEnabled: style.keywordHighlightEnabled,
    keywordColor: style.keywordColor,
    aiEmojiEnabled: style.aiEmojiEnabled,
    positionX: style.positionX,
    positionY: style.positionY,
    maxLines: style.maxLines,
    maskProfanity: style.maskProfanity,
  }
}

/**
 * Build CaptionTrack from legacy captions and style.
 */
export function buildCaptionTrack(
  captions: Caption[],
  style: CaptionStyle
): Track {
  const segments: CaptionSegment[] = captions.map((caption) => {
    const startMs = secondsToMs(caption.startTime)
    const endMs = secondsToMs(caption.endTime)

    const textElements = caption.words?.map((word) => {
      const { timelineIn, timelineOut } = toCaptionTimedWord(word)
      return {
        id: generateId("text"),
        text: word.word,
        duration: buildDuration(timelineIn, timelineOut),
        timeline: buildTimeline(timelineIn, timelineOut),
        color: 0 as 0 | 1 | 2,
        deleted: false,
      }
    }) || [{
      id: generateId("text"),
      text: caption.text,
      duration: buildDuration(startMs, endMs),
      timeline: buildTimeline(startMs, endMs),
      color: 0 as 0 | 1 | 2,
      deleted: false,
    }]

    return {
      id: caption.id || generateId("segment"),
      contentType: "Caption" as const,
      duration: buildDuration(startMs, endMs),
      timeline: buildTimeline(startMs, endMs),
      content: {
        id: generateId("content"),
        textElements,
        duration: buildDuration(startMs, endMs),
        timeline: buildTimeline(startMs, endMs),
      },
      position: buildPosition(0, 0.8, 0.9, 0.1),
    }
  })

  const totalEndMs = segments.length > 0
    ? segments[segments.length - 1].duration.eO
    : 0

  const section: Section = {
    id: generateId("section"),
    sectionDuration: buildDuration(0, totalEndMs),
    sectionTimeline: buildTimeline(0, totalEndMs),
    segments,
  }

  return {
    id: generateId("track"),
    trackType: "CaptionTrack",
    visible: true,
    locked: false,
    sections: segments.length > 0 ? [section] : [],
    styleConfig: convertStyleToConfig(style),
  }
}

/**
 * Build BRollTrack from legacy B-roll segments.
 */
export function buildBRollTrack(brollSegments: BRollSegment[]): Track {
  const segments: BRollSegmentType[] = brollSegments.map((segment) => ({
    id: segment.id || generateId("segment"),
    contentType: "BRoll" as const,
    duration: buildDuration(segment.startMs, segment.endMs),
    timeline: buildTimeline(segment.startMs, segment.endMs),
    ...(segment.trimStartMs ? { trimStartMs: segment.trimStartMs } : {}),
    content: {
      id: generateId("content"),
      brollType: segment.brollType,
      mediaInfo: segment.mediaInfo,
      prompt: segment.prompt,
    },
  }))

  const totalEndMs = segments.length > 0
    ? segments[segments.length - 1].duration.eO
    : 0

  const section: Section = {
    id: generateId("section"),
    sectionDuration: buildDuration(0, totalEndMs),
    sectionTimeline: buildTimeline(0, totalEndMs),
    segments,
  }

  return {
    id: generateId("track"),
    trackType: "BRollTrack",
    visible: true,
    locked: false,
    sections: segments.length > 0 ? [section] : [],
  }
}

/**
 * Build empty HookTrack.
 */
export function buildEmptyHookTrack(): Track {
  return {
    id: generateId("track"),
    trackType: "HookTrack",
    visible: true,
    locked: false,
    sections: [],
  }
}

/**
 * Build HookTrack from HookSegment array.
 */
export function buildHookTrack(segments: HookSegment[]): Track {
  if (segments.length === 0) {
    return buildEmptyHookTrack()
  }

  const totalEndMs = Math.max(...segments.map(s => s.timeline.out))

  const section: Section = {
    id: generateId("section"),
    sectionDuration: buildDuration(0, totalEndMs),
    sectionTimeline: buildTimeline(0, totalEndMs),
    segments: segments,
  }

  return {
    id: generateId("track"),
    trackType: "HookTrack",
    visible: true,
    locked: false,
    sections: [section],
  }
}

/**
 * Convert legacy clip data to full editingScript format.
 */
export function convertLegacyToEditingScript(
  captions: Caption[],
  style: CaptionStyle,
  brollSegments: BRollSegment[] = []
): EditingScript {
  return {
    version: "1.0",
    tracks: [
      buildCaptionTrack(captions, style),
      buildEmptyHookTrack(),
      buildBRollTrack(brollSegments),
    ],
    renderPref: {
      enableCaption: true,
      enableBroll: brollSegments.length > 0,
      enableHook: false,
      enableVoiceover: false,
      enableEmoji: false,
      enableMusic: false,
      enableGif: false,
      enableReframe: false,
      layoutAspectRatio: "portrait",
    },
  }
}

// =============================================================================
// Hook Segment Builder
// =============================================================================

/**
 * Create a new HookSegment (text overlay).
 */
export function createHookSegment(
  text: string,
  startMs: number,
  endMs: number,
  options: {
    position?: "top" | "middle" | "bottom"
    textStyle?: Partial<TextStyle>
  } = {}
): HookSegment {
  const { position = "top", textStyle = {} } = options

  const positionMap = {
    top: { xPct: 0, yPct: -0.6, wPct: 0.9, hPct: 0.15 },
    middle: { xPct: 0, yPct: 0, wPct: 0.9, hPct: 0.15 },
    bottom: { xPct: 0, yPct: 0.6, wPct: 0.9, hPct: 0.15 },
  }

  const defaultTextStyle: TextStyle = {
    backgroundColor: "rgba(0,0,0,0.7)",
    fontFamily: "Montserrat",
    fontWeight: "900",
    fontColor: "#FFFFFF",
    fontSize: 48,
    italic: false,
    borderRadius: 8,
    padding: 12,
    textAlign: "center",
  }

  return {
    id: generateId("segment"),
    contentType: "HookTextOverlay",
    duration: buildDuration(startMs, endMs),
    timeline: buildTimeline(startMs, endMs),
    content: {
      id: generateId("content"),
      textElements: [{
        id: generateId("text"),
        text,
        duration: buildDuration(startMs, endMs),
        timeline: buildTimeline(startMs, endMs),
        color: 0,
      }],
      textOverlayType: "paragraph",
      textStyle: { ...defaultTextStyle, ...textStyle },
      scale: 1.0,
      rotation: 0,
      duration: buildDuration(startMs, endMs),
      timeline: buildTimeline(startMs, endMs),
    },
    position: buildPosition(
      positionMap[position].xPct,
      positionMap[position].yPct,
      positionMap[position].wPct,
      positionMap[position].hPct
    ),
  }
}

// =============================================================================
// Voiceover Track Builder
// =============================================================================

/**
 * Build empty VoiceoverTrack.
 */
export function buildEmptyVoiceoverTrack(): Track {
  return {
    id: generateId("track"),
    trackType: "VoiceoverTrack",
    visible: true,
    locked: false,
    sections: [],
  }
}

/**
 * Build VoiceoverTrack from VoiceoverSegment array.
 */
export function buildVoiceoverTrack(segments: VoiceoverSegment[]): Track {
  if (segments.length === 0) {
    return buildEmptyVoiceoverTrack()
  }

  const totalEndMs = Math.max(...segments.map(s => s.timeline.out))

  const section: Section = {
    id: generateId("section"),
    sectionDuration: buildDuration(0, totalEndMs),
    sectionTimeline: buildTimeline(0, totalEndMs),
    segments: segments,
  }

  return {
    id: generateId("track"),
    trackType: "VoiceoverTrack",
    visible: true,
    locked: false,
    sections: [section],
  }
}

/**
 * Normalize a voiceover segment to handle snake_case from backend.
 * Converts snake_case keys to camelCase.
 */
function normalizeVoiceoverSegment(segment: Record<string, unknown>): VoiceoverSegment {
  // Handle snake_case content_type from backend
  const contentType = (segment.contentType || segment.content_type || "Voiceover") as "Voiceover"

  // Get content (might be snake_case from backend)
  const rawContent = (segment.content || {}) as Record<string, unknown>

  // Get mediaInfo with snake_case fallbacks
  const rawMediaInfo = (rawContent.mediaInfo || rawContent.media_info || {}) as Record<string, unknown>
  const mediaInfo = {
    mediaType: (rawMediaInfo.mediaType || rawMediaInfo.media_type || "audio") as "audio",
    originUrl: (rawMediaInfo.originUrl || rawMediaInfo.origin_url || "") as string,
    previewUrl: (rawMediaInfo.previewUrl || rawMediaInfo.preview_url || "") as string,
    duration: (rawMediaInfo.duration || 0) as number,
    voiceId: (rawMediaInfo.voiceId || rawMediaInfo.voice_id || "") as string,
    voiceName: (rawMediaInfo.voiceName || rawMediaInfo.voice_name || "") as string,
    scriptText: (rawMediaInfo.scriptText || rawMediaInfo.script_text || "") as string,
    wordCount: (rawMediaInfo.wordCount || rawMediaInfo.word_count || 0) as number,
  }

  // Get audioSettings with snake_case fallbacks
  const rawAudioSettings = (rawContent.audioSettings || rawContent.audio_settings || {}) as Record<string, unknown>
  const audioSettings = {
    volume: (rawAudioSettings.volume ?? 100) as number,
    duckOriginalAudio: (rawAudioSettings.duckOriginalAudio ?? rawAudioSettings.duck_original_audio ?? true) as boolean,
    duckLevel: (rawAudioSettings.duckLevel ?? rawAudioSettings.duck_level ?? 30) as number,
  }

  // Get voiceoverType with snake_case fallback
  const voiceoverType = (rawContent.voiceoverType || rawContent.voiceover_type || "hook") as "hook" | "cta" | "custom" | "distributed"

  // Get duration with snake_case fallbacks
  const rawDuration = (segment.duration || {}) as Record<string, unknown>
  const duration = {
    sO: (rawDuration.sO ?? rawDuration.s_o ?? 0) as number,
    eO: (rawDuration.eO ?? rawDuration.e_o ?? 0) as number,
    type: "TS" as const,
  }

  // Get timeline
  const rawTimeline = (segment.timeline || {}) as Record<string, unknown>
  const timeline = {
    in: (rawTimeline.in ?? 0) as number,
    out: (rawTimeline.out ?? 0) as number,
  }

  return {
    id: (segment.id || generateId("vo")) as string,
    contentType,
    duration,
    timeline,
    ...(typeof segment.trimStartMs === "number" && { trimStartMs: segment.trimStartMs }),
    content: {
      id: (rawContent.id || generateId("vo-content")) as string,
      voiceoverType,
      mediaInfo,
      audioSettings,
    },
  }
}

/**
 * Extract VoiceoverSegment[] from VoiceoverTrack.
 */
export function extractVoiceoversFromTrack(track: Track | undefined): VoiceoverSegment[] {
  if (!track || track.trackType !== "VoiceoverTrack") return []

  // Get raw segments directly from sections to avoid type constraint issues
  // and normalize them to handle snake_case from backend
  const rawSegments: Record<string, unknown>[] = []
  for (const section of track.sections) {
    for (const segment of section.segments) {
      // Cast to Record to allow flexible property access for normalization
      rawSegments.push(segment as unknown as Record<string, unknown>)
    }
  }
  return rawSegments.map(normalizeVoiceoverSegment)
}

/**
 * Create a VoiceoverSegment from API response data.
 */
export function createVoiceoverSegment(
  voiceoverData: {
    voiceover_type: "hook" | "cta" | "custom" | "distributed"
    script_text: string
    word_count: number
    duration_ms: number
    audio_url: string
    preview_url: string
    voice_id: string
    voice_name: string
    timeline_in: number
    timeline_out: number
    segment_id: string
    content_id: string
  },
  audioSettings: {
    volume: number
    duckOriginalAudio: boolean
    duckLevel: number
  }
): VoiceoverSegment {
  return {
    id: voiceoverData.segment_id,
    contentType: "Voiceover",
    duration: buildDuration(voiceoverData.timeline_in, voiceoverData.timeline_out),
    timeline: buildTimeline(voiceoverData.timeline_in, voiceoverData.timeline_out),
    content: {
      id: voiceoverData.content_id,
      voiceoverType: voiceoverData.voiceover_type,
      mediaInfo: {
        mediaType: "audio",
        originUrl: voiceoverData.audio_url,
        previewUrl: voiceoverData.preview_url,
        duration: voiceoverData.duration_ms,
        voiceId: voiceoverData.voice_id,
        voiceName: voiceoverData.voice_name,
        scriptText: voiceoverData.script_text,
        wordCount: voiceoverData.word_count,
      },
      audioSettings: {
        volume: audioSettings.volume,
        duckOriginalAudio: audioSettings.duckOriginalAudio,
        duckLevel: audioSettings.duckLevel,
      },
    },
  }
}

// =============================================================================
// Voiceover Overlap Detection
// =============================================================================

/**
 * Check if a new voiceover time range overlaps with existing voiceovers.
 *
 * @param existingSegments - Array of existing VoiceoverSegments
 * @param newStart - Start time of new voiceover in ms
 * @param newEnd - End time of new voiceover in ms
 * @returns Object with overlap status and conflicting segment if found
 */
export function checkVoiceoverOverlap(
  existingSegments: VoiceoverSegment[],
  newStart: number,
  newEnd: number
): { overlaps: boolean; conflictWith?: VoiceoverSegment } {
  for (const segment of existingSegments) {
    const existingStart = segment.timeline?.in || 0
    const existingEnd = segment.timeline?.out || 0

    // Check if ranges overlap: new starts before existing ends AND new ends after existing starts
    if (newStart < existingEnd && newEnd > existingStart) {
      return { overlaps: true, conflictWith: segment }
    }
  }
  return { overlaps: false }
}

/**
 * Time slot representing an available range for voiceover placement.
 */
export interface AvailableTimeSlot {
  start: number
  end: number
  durationMs: number
}

/**
 * Get available time slots for voiceover placement (gaps between existing voiceovers).
 *
 * @param existingSegments - Array of existing VoiceoverSegments
 * @param clipDurationMs - Total clip duration in milliseconds
 * @returns Array of available time slots
 */
export function getAvailableTimeSlots(
  existingSegments: VoiceoverSegment[],
  clipDurationMs: number
): AvailableTimeSlot[] {
  if (existingSegments.length === 0) {
    return [{ start: 0, end: clipDurationMs, durationMs: clipDurationMs }]
  }

  // Sort segments by start time
  const sorted = [...existingSegments].sort(
    (a, b) => (a.timeline?.in || 0) - (b.timeline?.in || 0)
  )

  const slots: AvailableTimeSlot[] = []

  // Check for gap at the beginning
  const firstStart = sorted[0].timeline?.in || 0
  if (firstStart > 0) {
    slots.push({
      start: 0,
      end: firstStart,
      durationMs: firstStart,
    })
  }

  // Check for gaps between segments
  for (let i = 0; i < sorted.length - 1; i++) {
    const currentEnd = sorted[i].timeline?.out || 0
    const nextStart = sorted[i + 1].timeline?.in || 0

    if (nextStart > currentEnd) {
      slots.push({
        start: currentEnd,
        end: nextStart,
        durationMs: nextStart - currentEnd,
      })
    }
  }

  // Check for gap at the end
  const lastEnd = sorted[sorted.length - 1].timeline?.out || 0
  if (lastEnd < clipDurationMs) {
    slots.push({
      start: lastEnd,
      end: clipDurationMs,
      durationMs: clipDurationMs - lastEnd,
    })
  }

  return slots
}

/**
 * Format milliseconds as a time string (M:SS.mm).
 */
export function formatTimeMs(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  const milliseconds = Math.floor((ms % 1000) / 10)
  return `${minutes}:${seconds.toString().padStart(2, "0")}.${milliseconds.toString().padStart(2, "0")}`
}

/**
 * Parse a time string (M:SS.mm) to milliseconds.
 */
export function parseTimeToMs(timeStr: string): number {
  const parts = timeStr.split(":")
  if (parts.length !== 2) return 0
  const [minutes, secondsPart] = parts
  const [seconds, ms] = secondsPart.split(".")
  return (
    parseInt(minutes || "0") * 60 * 1000 +
    parseInt(seconds || "0") * 1000 +
    parseInt(ms || "0") * 10
  )
}

// =============================================================================
// Music Track Builder
// =============================================================================

/**
 * Default audio settings for background music: 50%, ducked to half (25% overall) under speech.
 * The old 30% × 20% duck left 6% under speech, which sounded silent.
 */
export const DEFAULT_MUSIC_AUDIO_SETTINGS: MusicAudioSettings = {
  volume: 50,
  fadeInMs: 500,
  fadeOutMs: 500,
  loop: false,
  duckUnderSpeech: true,
  duckLevel: 50,
}

/**
 * Build empty MusicTrack.
 */
export function buildEmptyMusicTrack(): Track {
  return {
    id: generateId("track"),
    trackType: "MusicTrack",
    visible: true,
    locked: false,
    sections: [],
  }
}

/**
 * Build MusicTrack from MusicSegment array.
 */
export function buildMusicTrack(segments: MusicSegment[]): Track {
  if (segments.length === 0) {
    return buildEmptyMusicTrack()
  }

  const totalEndMs = Math.max(...segments.map(s => s.timeline.out))

  const section: Section = {
    id: generateId("section"),
    sectionDuration: buildDuration(0, totalEndMs),
    sectionTimeline: buildTimeline(0, totalEndMs),
    segments: segments,
  }

  return {
    id: generateId("track"),
    trackType: "MusicTrack",
    visible: true,
    locked: false,
    sections: [section],
  }
}

/**
 * Normalize a music segment to handle snake_case from backend.
 * Converts snake_case keys to camelCase.
 */
function normalizeMusicSegment(segment: Record<string, unknown>): MusicSegment {
  // Handle snake_case content_type from backend
  const contentType = (segment.contentType || segment.content_type || "Music") as "Music"

  // Get content (might be snake_case from backend)
  const rawContent = (segment.content || {}) as Record<string, unknown>

  // Get mediaInfo with snake_case fallbacks
  const rawMediaInfo = (rawContent.mediaInfo || rawContent.media_info || {}) as Record<string, unknown>
  const mediaInfo: MusicMediaInfo = {
    mediaType: "audio",
    originUrl: (rawMediaInfo.originUrl || rawMediaInfo.origin_url || "") as string,
    previewUrl: (rawMediaInfo.previewUrl || rawMediaInfo.preview_url || "") as string,
    duration: (rawMediaInfo.duration || 0) as number,
    title: (rawMediaInfo.title || "") as string | undefined,
    artist: (rawMediaInfo.artist || "") as string | undefined,
    sourceId: (rawMediaInfo.sourceId || rawMediaInfo.source_id || "") as string | undefined,
    waveformData: (rawMediaInfo.waveformData || rawMediaInfo.waveform_data) as number[] | undefined,
    genre: (rawMediaInfo.genre) as string | undefined,
    coverImage: (rawMediaInfo.coverImage || rawMediaInfo.cover_image) as string | undefined,
  }

  // Get audioSettings with snake_case fallbacks
  const rawAudioSettings = (rawContent.audioSettings || rawContent.audio_settings || {}) as Record<string, unknown>
  const audioSettings: MusicAudioSettings = {
    volume: (rawAudioSettings.volume ?? 50) as number,
    fadeInMs: (rawAudioSettings.fadeInMs ?? rawAudioSettings.fade_in_ms ?? 500) as number,
    fadeOutMs: (rawAudioSettings.fadeOutMs ?? rawAudioSettings.fade_out_ms ?? 500) as number,
    loop: (rawAudioSettings.loop ?? false) as boolean,
    duckUnderSpeech: (rawAudioSettings.duckUnderSpeech ?? rawAudioSettings.duck_under_speech ?? true) as boolean,
    duckLevel: (rawAudioSettings.duckLevel ?? rawAudioSettings.duck_level ?? 50) as number,
  }

  // Get musicType with snake_case fallback
  const musicType = (rawContent.musicType || rawContent.music_type || "Stock") as "Stock" | "Uploaded"

  // Get duration with snake_case fallbacks
  const rawDuration = (segment.duration || {}) as Record<string, unknown>
  const duration: Duration = {
    sO: (rawDuration.sO ?? rawDuration.s_o ?? 0) as number,
    eO: (rawDuration.eO ?? rawDuration.e_o ?? 0) as number,
    type: "TS" as const,
  }

  // Get timeline
  const rawTimeline = (segment.timeline || {}) as Record<string, unknown>
  const timeline: Timeline = {
    in: (rawTimeline.in ?? 0) as number,
    out: (rawTimeline.out ?? 0) as number,
  }

  return {
    id: (segment.id || generateId("music")) as string,
    contentType,
    duration,
    timeline,
    ...(typeof segment.trimStartMs === "number" && { trimStartMs: segment.trimStartMs }),
    content: {
      id: (rawContent.id || generateId("music-content")) as string,
      musicType,
      mediaInfo,
      audioSettings,
    },
  }
}

/**
 * Extract MusicSegment[] from MusicTrack.
 */
export function extractMusicFromTrack(track: Track | undefined): MusicSegment[] {
  if (!track || track.trackType !== "MusicTrack") return []

  // Get raw segments directly from sections to avoid type constraint issues
  // and normalize them to handle snake_case from backend
  const rawSegments: Record<string, unknown>[] = []
  for (const section of track.sections) {
    for (const segment of section.segments) {
      // Cast to Record to allow flexible property access for normalization
      rawSegments.push(segment as unknown as Record<string, unknown>)
    }
  }
  return rawSegments.map(normalizeMusicSegment)
}

// =============================================================================
// Emoji Track Builder
// =============================================================================

import type { EmojiSegment } from "@/remotion/schemas"

/**
 * Build empty EmojiTrack.
 */
export function buildEmptyEmojiTrack(): Track {
  return {
    id: generateId("track"),
    trackType: "EmojiTrack",
    visible: true,
    locked: false,
    sections: [],
  }
}

/**
 * Build EmojiTrack from EmojiSegment array.
 */
export function buildEmojiTrack(segments: EmojiSegment[]): Track {
  if (segments.length === 0) {
    return buildEmptyEmojiTrack()
  }

  const totalEndMs = Math.max(...segments.map(s => s.timeline.out))

  const section: Section = {
    id: generateId("section"),
    sectionDuration: buildDuration(0, totalEndMs),
    sectionTimeline: buildTimeline(0, totalEndMs),
    segments: segments,
  }

  return {
    id: generateId("track"),
    trackType: "EmojiTrack",
    visible: true,
    locked: false,
    sections: [section],
  }
}

/**
 * Extract EmojiSegment[] from EmojiTrack.
 */
export function extractEmojisFromTrack(track: Track | undefined): EmojiSegment[] {
  if (!track || track.trackType !== "EmojiTrack") return []

  const segments: EmojiSegment[] = []
  for (const section of track.sections) {
    for (const segment of section.segments) {
      if (segment.contentType === "Emoji") {
        segments.push(segment as EmojiSegment)
      }
    }
  }
  return segments
}

/**
 * Create a MusicSegment from track data and settings.
 */
export function createMusicSegment(
  trackData: {
    sourceId: string
    title: string
    artist: string
    duration: number
    originUrl: string
    previewUrl: string
    coverImage?: string
    genre?: string
    waveformData?: number[]
  },
  startMs: number,
  endMs: number,
  audioSettings: Partial<MusicAudioSettings> = {}
): MusicSegment {
  const mergedAudioSettings: MusicAudioSettings = {
    ...DEFAULT_MUSIC_AUDIO_SETTINGS,
    ...audioSettings,
  }

  return {
    id: generateId("music"),
    contentType: "Music",
    duration: buildDuration(startMs, endMs),
    timeline: buildTimeline(startMs, endMs),
    content: {
      id: generateId("music-content"),
      musicType: "Stock",
      mediaInfo: {
        mediaType: "audio",
        originUrl: trackData.originUrl,
        previewUrl: trackData.previewUrl,
        duration: trackData.duration,
        title: trackData.title,
        artist: trackData.artist,
        sourceId: trackData.sourceId,
        waveformData: trackData.waveformData,
        genre: trackData.genre,
        coverImage: trackData.coverImage,
      },
      audioSettings: mergedAudioSettings,
    },
  }
}

// =============================================================================
// GIF Track Builder
// =============================================================================

// Keep the clip's speech at full volume under a GIF by default; users can lower it per GIF.
export const DEFAULT_GIF_AUDIO_SETTINGS: GifAudioSettings = {
  volume: 100,
  fadeInMs: 0,
  fadeOutMs: 200,
  duckOriginalAudio: false,
  duckLevel: 100,
}

/**
 * Build empty GifTrack.
 */
export function buildEmptyGifTrack(): Track {
  return {
    id: generateId("track"),
    trackType: "GifTrack",
    visible: true,
    locked: false,
    sections: [],
  }
}

/**
 * Build GifTrack from GifSegmentType array.
 */
export function buildGifTrack(segments: GifSegmentType[]): Track {
  if (segments.length === 0) {
    return buildEmptyGifTrack()
  }

  const totalEndMs = Math.max(...segments.map(s => s.timeline.out))

  const section: Section = {
    id: generateId("section"),
    sectionDuration: buildDuration(0, totalEndMs),
    sectionTimeline: buildTimeline(0, totalEndMs),
    segments: segments,
  }

  return {
    id: generateId("track"),
    trackType: "GifTrack",
    visible: true,
    locked: false,
    sections: [section],
  }
}

/**
 * GIFs restored from an older snapshot (undo/redo): an add the backend has since confirmed loses
 * `pending`, a refused one is dropped, and one still in flight stays pending.
 */
export function settlePendingGifs(
  segments: GifSegmentType[],
  confirmed: ReadonlySet<string>,
  failed: ReadonlySet<string>
): GifSegmentType[] {
  return segments
    .filter((s) => !(s.pending && failed.has(s.id)))
    .map((s) => (s.pending && confirmed.has(s.id) ? { ...s, pending: undefined } : s))
}

/** The script to save: a GIF the backend hasn't confirmed yet stays in the editor only. */
export function withoutPendingGifs<T extends { tracks: Track[] }>(script: T): T {
  return {
    ...script,
    tracks: script.tracks.map((track) =>
      track.trackType !== "GifTrack"
        ? track
        : { ...track, sections: track.sections.map((s) => ({ ...s, segments: s.segments.filter((seg) => !seg.pending) })) }
    ),
  }
}

/**
 * Extract GifSegmentType[] from GifTrack.
 */
export function extractGifsFromTrack(track: Track | undefined): GifSegmentType[] {
  if (!track || track.trackType !== "GifTrack") return []

  const segments: GifSegmentType[] = []
  for (const section of track.sections) {
    for (const segment of section.segments) {
      if (segment.contentType === "Gif") {
        segments.push(segment as GifSegmentType)
      }
    }
  }
  return segments
}

/**
 * Create a GifSegmentType from GIF clip data.
 */
export function createGifSegment(
  clipData: {
    clipId: string
    videoUrl: string
    thumbnailUrl: string
    durationSeconds: number
    source: string
    quote: string
  },
  startMs: number,
  audioSettings: Partial<GifAudioSettings> = {},
  position?: Position
): GifSegmentType {
  const durationMs = Math.floor(clipData.durationSeconds * 1000)
  const endMs = startMs + durationMs

  const mergedAudioSettings: GifAudioSettings = {
    ...DEFAULT_GIF_AUDIO_SETTINGS,
    ...audioSettings,
  }

  return {
    id: generateId("gif"),
    contentType: "Gif",
    duration: buildDuration(startMs, endMs),
    timeline: buildTimeline(startMs, endMs),
    content: {
      id: generateId("gif-content"),
      gifType: "Vlipsy",
      mediaInfo: {
        mediaType: "video",
        originUrl: clipData.videoUrl,
        previewUrl: clipData.thumbnailUrl,
        width: 0,
        height: 0,
        sourceId: `gif-${clipData.clipId}`,
        duration: durationMs,
        source: clipData.source || "",
        quote: clipData.quote || "",
      },
      audioSettings: mergedAudioSettings,
    },
    position: position || DEFAULT_GIF_POSITION,
  }
}
